import React from 'react';
import { Helmet } from 'react-helmet-async';
import { FileText } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Terms & Conditions | VietClinics</title>
        <meta name="description" content="Read the VietClinics Terms & Conditions — patient and clinic partner responsibilities, booking process, liability and platform rules." />
        <link rel="canonical" href="https://vietclinics.com/terms" />
      </Helmet>
      <Header />

      {/* Hero */}
      <div className="bg-gradient-to-r from-[#1E5BA8] to-[#1FA7A1] py-12">
        <div className="container mx-auto px-4 text-center">
          <FileText className="h-12 w-12 text-white mx-auto mb-4" />
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
            Terms & Conditions
          </h1>
          <p className="text-white/80">Last updated: March 2026</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-sm p-6 md:p-10">
          <div className="prose prose-gray max-w-none">
            
            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">1. Introduction</h2>
              <p className="text-gray-600 leading-relaxed">
                Welcome to VietClinics. These Terms and Conditions govern your use of our website and services. 
                By accessing or using VietClinics, you agree to be bound by these terms. If you disagree with 
                any part of these terms, please do not use our platform.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">2. Platform Description</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                VietClinics operates as a trusted healthcare platform connecting patients with clinics and medical 
                service providers in Vietnam. We provide a platform for:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Discovering and comparing healthcare providers</li>
                <li>Viewing clinic information, services, and pricing</li>
                <li>Submitting booking requests to clinics</li>
                <li>Reading and writing reviews</li>
              </ul>
              <p className="text-gray-600 leading-relaxed mt-4">
                <strong>Important:</strong> VietClinics is a trusted platform only. We do not provide 
                medical services directly and are not responsible for the medical care provided by clinics 
                listed on our platform.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">3. User Responsibilities</h2>
              <p className="text-gray-600 leading-relaxed mb-4">As a user of VietClinics, you agree to:</p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Provide accurate and truthful information when booking appointments</li>
                <li>Attend confirmed appointments or cancel with reasonable notice</li>
                <li>Treat clinic staff with respect and courtesy</li>
                <li>Pay for services rendered at the clinic</li>
                <li>Not misuse the platform for fraudulent purposes</li>
                <li>Provide honest and fair reviews of your experiences</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">4. Clinic Responsibilities</h2>
              <p className="text-gray-600 leading-relaxed mb-4">Clinics registered on VietClinics agree to:</p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Provide accurate information about services, pricing, and qualifications</li>
                <li>Maintain valid licenses and certifications as required by Vietnamese law</li>
                <li>Respond to booking requests in a timely manner</li>
                <li>Honor confirmed appointments and quoted prices</li>
                <li>Provide quality care that meets professional standards</li>
                <li>Handle patient data in accordance with privacy regulations</li>
                <li>Pay agreed commission fees to VietClinics</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">5. Booking & Payments</h2>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Booking requests submitted through VietClinics are not confirmed until the clinic accepts them</li>
                <li>Prices shown are starting prices and may vary based on individual treatment needs</li>
                <li>Payment for treatments is made directly to the clinic</li>
                <li>VietClinics does not process payments between patients and clinics</li>
                <li>Clinics may require deposits for certain treatments</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">6. Cancellation Policy</h2>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Patients should cancel appointments at least 24 hours in advance</li>
                <li>Repeated no-shows may result in account restrictions</li>
                <li>Individual clinics may have their own cancellation policies</li>
                <li>Refunds for deposits are subject to clinic policies</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">7. Limitation of Liability</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                <strong>VietClinics expressly disclaims liability for:</strong>
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Medical outcomes, complications, or results of treatments</li>
                <li>Actions, omissions, or conduct of clinics or healthcare providers</li>
                <li>Accuracy of information provided by clinics</li>
                <li>Disputes between patients and clinics</li>
                <li>Loss or damage arising from use of the platform</li>
              </ul>
              <p className="text-gray-600 leading-relaxed mt-4">
                Patients are responsible for conducting their own due diligence when selecting healthcare 
                providers. Always consult qualified medical professionals for health-related decisions.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">8. Intellectual Property</h2>
              <p className="text-gray-600 leading-relaxed">
                All content on VietClinics, including text, graphics, logos, and software, is the property 
                of VietClinics or its licensors. Unauthorized use, reproduction, or distribution is prohibited.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">9. Account Termination</h2>
              <p className="text-gray-600 leading-relaxed">
                VietClinics reserves the right to suspend or terminate accounts that violate these terms, 
                engage in fraudulent activity, or harm the reputation of the platform.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">10. Governing Law</h2>
              <p className="text-gray-600 leading-relaxed">
                These terms are governed by the laws of Vietnam. Any disputes shall be resolved in the 
                competent courts of Ho Chi Minh City, Vietnam.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">11. Changes to Terms</h2>
              <p className="text-gray-600 leading-relaxed">
                VietClinics may update these terms from time to time. Continued use of the platform after 
                changes constitutes acceptance of the new terms. We will notify users of significant changes 
                via email or platform notification.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-4">12. Contact Us</h2>
              <p className="text-gray-600 leading-relaxed">
                If you have questions about these Terms and Conditions, please contact us at:
              </p>
              <ul className="list-none mt-2 text-gray-600">
                <li>Email: legal@vietclinics.com</li>
                <li>Address: District 1, Ho Chi Minh City, Vietnam</li>
              </ul>
            </section>

          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TermsPage;
